import { useEffect, useRef } from 'react';

import {
  CountdownReloadToast,
  usePreventAutoReloadOnNewVersion as usePreventAutoReload,
} from '@eluve/blocks';
import { useToast } from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import { useCaptureEvent } from '@eluve/posthog-react';

const NEW_VERSION_TOAST_ID = 'new-version-toast';

const NEW_VERSION_EVENT_NAMES = {
  viewed: 'user_viewed_new_version_toast',
  userDismissed: 'user_dismissed_new_version_toast',
  forceDismissed: 'force_dismissed_new_version_toast',
  userReloaded: 'user_reloaded_for_new_version',
  autoReloaded: 'auto_reloaded_for_new_version',
} as const;

type VersionCheckerProps = {
  children: React.ReactNode;
  dismissDelayHours?: number;
  intervalMinutes?: number;
};

export const VersionChecker: React.FC<VersionCheckerProps> = ({
  children,
  dismissDelayHours = 24,
  intervalMinutes = 5,
}) => {
  const logger = useNamedLogger('VersionChecker');
  const versionRef = useRef<string | null>(null);
  const toastOpenRef = useRef<boolean>(false);
  const dismissDateRef = useRef<Date | null>(null);
  const captureEvent = useCaptureEvent();
  const toast = useToast();

  useEffect(() => {
    const checkVersion = async () => {
      const currentVersion = versionRef.current;
      const currentToastOpen = toastOpenRef.current;
      try {
        const headers = new Headers();
        if (currentVersion) {
          headers.append('If-None-Match', currentVersion);
        }
        const response = await fetch(window.location.href, {
          method: 'HEAD',
          headers,
        });

        const newVersion = response.headers.get('ETag');
        if (!newVersion) {
          return;
        }
        if (!currentVersion) {
          versionRef.current = newVersion;
          return;
        }

        const dismissDate = dismissDateRef.current;
        if (dismissDate) {
          const dismissDelayMs = dismissDelayHours * 60 * 60 * 1000;
          const timeSinceDismiss = Date.now() - dismissDate.getTime();
          if (timeSinceDismiss < dismissDelayMs) {
            return;
          }
        }

        const shouldPreventAutoReload = usePreventAutoReload
          .getState()
          .shouldPrevent();
        if (shouldPreventAutoReload) {
          logger.info(
            'Prevented prompting user to reload the page to get the latest updates',
            { newVersion, currentVersion },
          );
          return;
        }

        if (newVersion !== currentVersion && !currentToastOpen) {
          logger.info(
            'Prompting user to reload the page to get the latest updates',
            { newVersion, currentVersion },
          );

          dismissDateRef.current = null;
          toastOpenRef.current = true;

          const onDismiss = (forced: boolean) => {
            if (!forced) {
              dismissDateRef.current = new Date();
            }
            toastOpenRef.current = false;
          };

          toast.message(
            <CountdownReloadToast
              toastId={NEW_VERSION_TOAST_ID}
              title="New Version Available"
              description="You can start using the latest version of Eluve after a quick page reload."
              iconName="CircleFadingArrowUp"
              eventNames={NEW_VERSION_EVENT_NAMES}
              meta={{ newAppVersion: newVersion }}
              captureEvent={captureEvent}
              onDismiss={onDismiss}
            />,
            {
              id: NEW_VERSION_TOAST_ID,
              position: 'bottom-right',
              duration: Infinity,
              onDismiss: () => onDismiss(false),
            },
          );
        }
      } catch (error) {
        logger.warn('Error checking version:', {
          error: (error as Error)?.message,
        });
      }
    };
    const intervalId = setInterval(checkVersion, intervalMinutes * 60 * 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, [captureEvent, dismissDelayHours, intervalMinutes, logger, toast]);

  return children;
};
